import { useState } from 'react'
import { ApiError } from './api.ts'

/**
 * Pieces the People page and the Team page share: the one-time link panel and
 * error reporting. An invitation or reset link is shown exactly once — what is
 * stored is its SHA-256 — so the panel that shows it must behave the same
 * wherever it appears.
 */

export interface Issued {
  label: string
  url: string
}

/** The link an administrator hands over. Built here because only the browser knows the origin. */
export function linkFor(path: string, token: string): string {
  return `${window.location.origin}${path}?token=${encodeURIComponent(token)}`
}

export function useIssueState() {
  const [issued, setIssued] = useState<Issued | null>(null)
  const [error, setError] = useState<string | null>(null)

  function report(caught: unknown) {
    setError(
      caught instanceof ApiError || caught instanceof Error ? caught.message : 'That did not work.',
    )
  }

  return { issued, setIssued, error, setError, report }
}

export function ErrorNote({ error }: { error: string | null }) {
  if (!error) return null
  return (
    <p role="alert" className="mt-4 rounded-md border border-line bg-surface-sunken px-3 py-2 text-sm text-ink">
      {error}
    </p>
  )
}

export function IssuedLink({ issued, onDismiss }: { issued: Issued | null; onDismiss: () => void }) {
  if (!issued) return null
  return (
    <section className="mt-4 rounded-lg border border-gold-500 bg-surface-sunken p-4">
      <h2 className="text-sm font-semibold">{issued.label}</h2>
      <p className="mt-1 text-xs text-ink-muted">
        Copy this now — it is shown once and cannot be recovered. Send it by a channel you trust,
        and confirm who you are speaking to before you do.
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <code className="min-w-0 flex-1 overflow-x-auto rounded border border-line bg-surface px-2 py-1.5 text-xs">
          {issued.url}
        </code>
        <button
          type="button"
          onClick={() => void navigator.clipboard.writeText(issued.url)}
          className="rounded-md border border-line px-2.5 py-1.5 text-xs font-semibold hover:border-brand-400"
        >
          Copy
        </button>
        <button type="button" onClick={onDismiss} className="text-xs text-ink-muted hover:text-ink">
          Dismiss
        </button>
      </div>
    </section>
  )
}

export const LABEL = 'mb-1 block text-xs font-medium tracking-wide text-ink-muted uppercase'
export const INPUT = 'rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-ink'
export const BUTTON =
  'rounded-md bg-brand-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-50'
export const LINK_BUTTON = 'text-xs font-semibold text-brand-700 hover:underline'
